import React, { useEffect, useState } from 'react';
import { Briefcase, ChevronRight } from 'lucide-react';
import { listPortalMatters } from '../../lib/db';
import { formatDate } from '../../lib/formatters';
import { useRoute } from '../../lib/router';

const STATUS_COLOR = {
  open:        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  closed:      'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  archived:    'bg-slate-100 text-slate-500',
};

export default function PortalMattersPage() {
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, navigate] = useRoute();

  useEffect(() => {
    listPortalMatters().then(({ data }) => {
      setMatters(data || []);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Your Case Files</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Loading…</p>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-5 py-4 animate-pulse">
              <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
              <div className="h-4 w-2/3 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
              <div className="h-3 w-1/2 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Your Case Files</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{matters.length} case file{matters.length !== 1 ? 's' : ''} on file</p>
      </div>

      {matters.length === 0 ? (
        <div className="text-center py-20 max-w-md mx-auto text-slate-500">
          <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
            <Briefcase size={28} className="text-slate-400" />
          </div>
          <p className="font-medium text-slate-700 dark:text-slate-300">No case files yet</p>
          <p className="text-sm mt-2">Once your attorney opens a case file for you, you'll see its status, invoices, and any shared documents here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {matters.map((m) => (
            <button
              key={m.id}
              onClick={() => navigate(`/portal/matters/${m.id}`)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-5 py-4 text-left hover:shadow-md transition group"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${STATUS_COLOR[m.status] || STATUS_COLOR.open}`}>
                      {m.status?.replace('_', ' ')}
                    </span>
                    <span className="text-xs text-slate-400">{m.matter_number}</span>
                  </div>
                  <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition">
                    {m.title}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500">
                    {m.practice_area && <span>{m.practice_area}</span>}
                    {m.jurisdiction   && <span>{m.jurisdiction}</span>}
                    {m.court          && <span>{m.court}</span>}
                    <span>Opened {formatDate(m.open_date || m.created_at)}</span>
                  </div>
                  {m.description && (
                    <p className="mt-2 text-sm text-slate-400 line-clamp-2">{m.description}</p>
                  )}
                </div>
                <ChevronRight size={18} className="text-slate-300 group-hover:text-indigo-500 transition flex-shrink-0 mt-1" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
